import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaCheck, FaTimes, FaSpinner } from 'react-icons/fa';

const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const PopupContent = styled.div`
  background-color: var(--card-bg);
  padding: 2rem;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const StatusTitle = styled.h2`
  margin-bottom: 1.5rem;
  color: var(--text-primary);
  text-align: center;
`;

const StatusList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const StatusItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  padding: 0.5rem;
  border-radius: 4px;
  background-color: ${props => props.status === 'loading' ? 'var(--bg-secondary)' : 
    props.status === 'success' ? 'var(--success-bg)' : 'var(--error-bg)'};
  transition: all 0.3s ease;
`;

const StatusIcon = styled.div`
  margin-right: 1rem;
  color: ${props => props.status === 'loading' ? 'var(--text-secondary)' : 
    props.status === 'success' ? 'var(--success)' : 'var(--error)'};
`;

const StatusText = styled.div`
  flex: 1;
`;

const StatusLabel = styled.div`
  font-weight: 500;
  margin-bottom: 0.25rem;
  color: var(--text-primary);
`;

const StatusMessage = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
`;

const PopupFooter = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
`;

const CreateNewOrderButton = styled.button`
  background-color: var(--primary-color);
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  width: 100%;

  &:hover {
    background-color: var(--primary-dark);
  }
`;

const OrderStatusPopup = ({ apiResults, onClose }) => {
  const [statuses, setStatuses] = useState({
    order: { status: 'loading', message: 'Creating order...' },
    ghl_api: { status: 'loading', message: 'Sending notes to GHL...' },
    custom_field: { status: 'loading', message: 'Updating custom fields...' },
    prognocis_udf: { status: 'loading', message: 'Updating Prognocis UDF...' },
    zapier_notes: { status: 'loading', message: 'Sending data to Zapier...' }
  });

  useEffect(() => {
    if (apiResults) {
      const newStatuses = { ...statuses };
      
      // Update each status based on API results
      Object.keys(apiResults).forEach(key => {
        if (key !== 'overall_status' && key !== 'errors') {
          const result = apiResults[key];
          newStatuses[key] = {
            status: result.status || result.success ? 'success' : 'error',
            message: result.msg || (result.success ? 'Success' : 'Failed')
          };
        }
      });

      setStatuses(newStatuses);
    }
  }, [apiResults]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'loading':
        return <FaSpinner className="fa-spin" />;
      case 'success':
        return <FaCheck />;
      case 'error':
        return <FaTimes />;
      default:
        return null;
    }
  };

  const handleCreateNewOrder = () => {
    onClose();
    window.location.href = '/orders/create';
  };

  return (
    <PopupOverlay onClick={onClose}>
      <PopupContent onClick={e => e.stopPropagation()}>
        <StatusTitle>Order Creation Status</StatusTitle>
        <StatusList>
          {Object.entries(statuses).map(([key, value]) => (
            <StatusItem key={key} status={value.status}>
              <StatusIcon status={value.status}>
                {getStatusIcon(value.status)}
              </StatusIcon>
              <StatusText>
                <StatusLabel>
                  {key === 'order' ? 'Order Creation' :
                   key === 'ghl_api' ? 'GHL Notes' :
                   key === 'custom_field' ? 'Custom Fields' :
                   key === 'receipt_webhook' ? 'GHL Order Receipt' :
                   key === 'ghl_order_webhook' ? 'GHL Order Info' :
                   key === 'prognocis_udf' ? 'Prognocis UDF Update' :
                   'Zapier Integration'}
                </StatusLabel>
                <StatusMessage>{value.message}</StatusMessage>
              </StatusText>
            </StatusItem>
          ))}
        </StatusList>
        <PopupFooter>
          <CreateNewOrderButton onClick={handleCreateNewOrder}>
            Create New Order
          </CreateNewOrderButton>
        </PopupFooter>
      </PopupContent>
    </PopupOverlay>
  );
};

export default OrderStatusPopup;